/* eslint-disable import/no-named-as-default-member */
/* eslint-disable import/no-named-as-default */
import React, { useState } from 'react';
import SelectButton from 'components/atoms/select_button';
import { DocumentData, DocumentsGroup } from 'store/modules/documents_store/types';
import moment from 'moment';
import ModalEditDoc from 'components/atoms/modal_edit_doc';
import DeleteDocumentModal from 'components/atoms/delete_document_modal';
import DocumentActionsButton from 'components/atoms/document_actions_button';
import TimeAuth from 'assets/images/time-auth.svg';
import ErrorAuth from 'assets/images/error-auth.svg';
import BlockAuth from 'assets/images/block-auth.svg';
import CheckAuth from 'assets/images/check-auth.svg';

interface DocumentItemProps {
  document: DocumentData;
  documentGroupList: Array<DocumentsGroup>;
  toggleSelectedDocuments: (id: number) => void;
  selectedDocuments: Array<number>;
  setModalAuthenticateDocumentoOpened: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentDocumentId: React.Dispatch<React.SetStateAction<string>>;
  setShowModalWarningAdditional: React.Dispatch<React.SetStateAction<boolean>>;
  setDocumentSelectedForPreview: React.Dispatch<React.SetStateAction<DocumentData | null>>;
}

const getDocumentId = (document: DocumentData): number =>
  Number(document?.default?.id || document?.id);

const getRowClass = (document: DocumentData): string => {
  if (moment(document.expirationDate).isBefore(moment(), 'day')) return 'alert-danger';
  if (document.statusLabel === 'Indisponível') return 'alert-warning';
  return '';
};

const getBadgeClass = (status: string): string => {
  if (status === 'PendenteInscrição') return 'badge-secondary';
  if (status === 'Indisponível') return 'badge-warning';
  if (status === 'Coletando') return 'badge-info';
  if (status === 'Pendente') return 'badge-secondary';
  return 'badge-success';
};

const DocumentItem = (props: DocumentItemProps) => {
  const {
    document,
    documentGroupList,
    toggleSelectedDocuments,
    selectedDocuments,
    setModalAuthenticateDocumentoOpened,
    setCurrentDocumentId,
    setShowModalWarningAdditional,
    setDocumentSelectedForPreview,
  } = props;
  const [deleteDocumentModalOpened, setDeleteDocumentModalOpened] = useState(false);
  const [toBeDeletedDocumentId, setToBeDeletedDocumentId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [doc, setDoc] = useState<any>({});

  const adjustDocumentName = (documentTitle: string): string => {
    const adjustedNames: Record<string, string> = {
      'Certidão de Débitos Não Inscritos': 'Certidão de Débitos Estaduais Não Inscritos',
      'Certidão Negativa Simplificada': 'Certidão Simplificada da Junta Comercial',
    };
    return adjustedNames[documentTitle] || documentTitle;
  };

  const handleEditDoc = (doc: DocumentData) => {
    setShowModal(true);
    setDoc(doc);
  };

  const showIconMsg = (document: DocumentData) => {
    if (document.authenticationStatus === 'pendente')
      return 'Documento em processo de autenticação. Isso pode levar alguns dias';
    if (document.authenticationStatus === 'autenticado') return 'Documento autenticado.';
    if (document.authenticationStatus === 'erro')
      return 'Não foi possível autenticar esse documento. Tente novamente.';
    if (document?.fileUrl?.split('.')[document.fileUrl.split('.').length - 1] !== 'pdf')
      return 'O tipo desse documento não pode ser autenticado. Só é possível autenticar documentos do tipo pdf.';
    return '';
  };

  return (
    <div className={`row py-2 border-bottom align-items-center ${getRowClass(document)}`}>
      <div className="col-1 text-center">
        {!(new Date(document.expirationDate).getTime() < Date.now()) && (
          <SelectButton
            id={getDocumentId(document)}
            isChecked={selectedDocuments.includes(getDocumentId(document))}
            toggleSelected={() => toggleSelectedDocuments(getDocumentId(document))}
            text=""
          />
        )}
      </div>
      <div className="col-2">
        <strong title={document?.name}>
          {document?.document_type_description || adjustDocumentName(document?.name)}
        </strong>
        <small className="text-muted d-block">{document?.description}</small>
      </div>
      <div className="col-1 text-center">
        <span className={`badge ${document?.is_aws ? 'badge-info' : 'badge-success'}`}>
          {document?.is_aws ? 'Manual' : 'Auto'}
        </span>
      </div>
      <div className="col-2 text-center">{document?.document_group}</div>
      <div className="col-1 text-center">
        <span className={`py-1 px-2 badge ${getBadgeClass(document.statusLabel)}`}>
          {document.statusLabel}
        </span>
      </div>
      <div className="col-2 text-center">
        {moment(document?.expirationDate).isValid()
          ? moment(document?.expirationDate).format('L')
          : '-'}
      </div>
      <div
        className="col-2 text-center databalloon"
        data-balloon-pos="up"
        data-balloon-length="large"
        aria-label={showIconMsg(document)}
      >
        {document.authenticationStatus === 'pendente' && (
          <img src={TimeAuth} alt="status da autenticação" />
        )}
        {document.authenticationStatus === 'autenticado' && (
          <img src={CheckAuth} alt="status da autenticação" />
        )}
        {document.authenticationStatus === 'erro' && (
          <img src={ErrorAuth} alt="status da autenticação" />
        )}

        {document?.fileUrl?.split('.')[document.fileUrl.split('.').length - 1] !== 'pdf' && (
          <img src={BlockAuth} alt="status da autenticação" />
        )}
      </div>
      <div className="col-1 p-0 text-center">
        {!(document?.statusLabel === 'Indisponível' || document?.statusLabel === 'Coletando') && (
          <DocumentActionsButton
            document={document}
            handleEditDoc={handleEditDoc}
            setToBeDeletedDocumentId={setToBeDeletedDocumentId}
            setDeleteDocumentModalOpened={setDeleteDocumentModalOpened}
            getDocumentId={getDocumentId}
            setModalAuthenticateDocumentoOpened={setModalAuthenticateDocumentoOpened}
            setCurrentDocumentId={setCurrentDocumentId}
            setShowModalWarningAdditional={setShowModalWarningAdditional}
            setDocumentSelectedForPreview={setDocumentSelectedForPreview}
          />
        )}
        <ModalEditDoc
          setShowModal={setShowModal}
          showModal={showModal}
          doc={doc}
          documentGroupList={documentGroupList}
          monitoredCompaniesSelected={null}
        />
        {deleteDocumentModalOpened && toBeDeletedDocumentId && (
          <DeleteDocumentModal
            documentId={toBeDeletedDocumentId}
            deleteDocumentModalOpened={deleteDocumentModalOpened}
            setDeleteDocumentModalOpened={setDeleteDocumentModalOpened}
          />
        )}
      </div>
    </div>
  );
};

export default DocumentItem;
